/**
 * Contract Dialog — Add/Edit employment contracts (Day Rate or Salaried)
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, Briefcase, PoundSterling } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractDialog = ({ open, onOpenChange, contract, onSaved }) => {
  const isEdit = !!contract;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    contract_type: 'day_rate',
    employer_name: '',
    client_name: '',
    start_date: '',
    end_date: '',
    daily_rate: '',
    annual_salary: '',
    contract_days: '',
    daily_uplift: '',
    travel_day_rate: '',
    hotel_day_rate: '',
    p45_date: '',
    p45_earnings: '',
    notes: '',
  });

  useEffect(() => {
    if (contract) {
      setForm({
        contract_type: contract.contract_type || 'day_rate',
        employer_name: contract.employer_name || '',
        client_name: contract.client_name || '',
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        daily_rate: contract.daily_rate ?? '',
        annual_salary: contract.annual_salary ?? '',
        contract_days: contract.contract_days ?? '',
        daily_uplift: contract.daily_uplift ?? '',
        travel_day_rate: contract.travel_day_rate ?? '',
        hotel_day_rate: contract.hotel_day_rate ?? '',
        p45_date: contract.p45_date || '',
        p45_earnings: contract.p45_earnings ?? '',
        notes: contract.notes || '',
      });
    } else {
      setForm({
        contract_type: 'day_rate',
        employer_name: '',
        client_name: '',
        start_date: '',
        end_date: '',
        daily_rate: '',
        annual_salary: '',
        contract_days: '',
        daily_uplift: '',
        p45_date: '',
        p45_earnings: '',
        notes: '',
      });
    }
  }, [contract, open]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Auto-calculated preview for salaried
  const monthlyBase = form.annual_salary ? (parseFloat(form.annual_salary) / 12).toFixed(2) : null;
  const postContractUplift = (form.annual_salary && form.contract_days && form.daily_uplift)
    ? ((parseFloat(form.annual_salary) / parseFloat(form.contract_days)) + parseFloat(form.daily_uplift)).toFixed(2)
    : null;

  const handleSubmit = async () => {
    if (!form.employer_name || !form.start_date || !form.end_date) {
      toast.error('Please fill in employer name and contract dates');
      return;
    }
    if (form.contract_type === 'day_rate' && !form.daily_rate) {
      toast.error('Please enter the daily rate');
      return;
    }
    if (form.contract_type === 'salaried' && (!form.annual_salary || !form.contract_days || !form.daily_uplift)) {
      toast.error('Please fill in salary, contract days, and daily uplift');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        contract_type: form.contract_type,
        employer_name: form.employer_name,
        client_name: form.client_name || null,
        start_date: form.start_date,
        end_date: form.end_date,
        daily_rate: form.daily_rate ? parseFloat(form.daily_rate) : null,
        annual_salary: form.annual_salary ? parseFloat(form.annual_salary) : null,
        contract_days: form.contract_days ? parseInt(form.contract_days) : null,
        daily_uplift: form.daily_uplift ? parseFloat(form.daily_uplift) : null,
        travel_day_rate: form.travel_day_rate ? parseFloat(form.travel_day_rate) : null,
        hotel_day_rate: form.hotel_day_rate ? parseFloat(form.hotel_day_rate) : null,
        p45_date: form.p45_date || null,
        p45_earnings: form.p45_earnings ? parseFloat(form.p45_earnings) : null,
        notes: form.notes || null,
      };

      if (isEdit) {
        await axios.put(`${API_URL}/api/contracts/${contract.id}`, payload);
        toast.success('Contract updated');
      } else {
        await axios.post(`${API_URL}/api/contracts`, payload);
        toast.success('Contract created');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-white">
            {isEdit ? 'Edit Contract' : 'Add Contract'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contract Type Toggle */}
          <div className="space-y-2">
            <Label className="text-slate-300">Contract Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={form.contract_type === 'day_rate' ? 'default' : 'outline'}
                className={form.contract_type === 'day_rate' ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-600 text-slate-300'}
                onClick={() => update('contract_type', 'day_rate')}
                data-testid="contract-type-day-rate"
              >
                Day Rate
              </Button>
              <Button
                type="button"
                variant={form.contract_type === 'salaried' ? 'default' : 'outline'}
                className={form.contract_type === 'salaried' ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-600 text-slate-300'}
                onClick={() => update('contract_type', 'salaried')}
                data-testid="contract-type-salaried"
              >
                Salaried
              </Button>
            </div>
          </div>

          {/* Employer & Client */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-sm">Employer Name *</Label>
              <Input
                value={form.employer_name}
                onChange={e => update('employer_name', e.target.value)}
                placeholder="e.g. Archer Offshore"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="contract-employer"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-sm">Client (optional)</Label>
              <Input
                value={form.client_name}
                onChange={e => update('client_name', e.target.value)}
                placeholder="e.g. Shell, BP"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="contract-client"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-sm">Start Date *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={e => update('start_date', e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="contract-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-sm">End Date *</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={e => update('end_date', e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="contract-end-date"
              />
            </div>
          </div>

          {/* Day Rate Fields */}
          {form.contract_type === 'day_rate' && (
            <div className="space-y-1">
              <Label className="text-slate-300 text-sm">Daily Rate (£) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.daily_rate}
                onChange={e => update('daily_rate', e.target.value)}
                placeholder="e.g. 581"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="contract-daily-rate"
              />
            </div>
          )}

          {/* Salaried Fields */}
          {form.contract_type === 'salaried' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-300 text-sm">Annual Salary (£) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.annual_salary}
                    onChange={e => update('annual_salary', e.target.value)}
                    placeholder="e.g. 30000"
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid="contract-annual-salary"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-300 text-sm">Contract Days *</Label>
                  <Input
                    type="number"
                    value={form.contract_days}
                    onChange={e => update('contract_days', e.target.value)}
                    placeholder="e.g. 120"
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid="contract-days"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Daily Uplift (£/day worked) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.daily_uplift}
                  onChange={e => update('daily_uplift', e.target.value)}
                  placeholder="e.g. 100"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="contract-daily-uplift"
                />
              </div>

              {/* Auto-calculated preview */}
              {monthlyBase && (
                <div className="p-3 bg-slate-900/60 rounded-lg border border-slate-700 space-y-1.5">
                  <p className="text-xs text-slate-400 font-medium">Auto-calculated</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Monthly base pay</span>
                    <span className="text-amber-400 font-medium">£{parseFloat(monthlyBase).toLocaleString('en-GB')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Standard uplift (days 1–{form.contract_days || '?'})</span>
                    <span className="text-amber-400 font-medium">£{parseFloat(form.daily_uplift || 0).toLocaleString('en-GB')}/day</span>
                  </div>
                  {postContractUplift && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Post-contract uplift (day {parseInt(form.contract_days || 0) + 1}+)</span>
                      <span className="text-emerald-400 font-medium">£{parseFloat(postContractUplift).toLocaleString('en-GB')}/day</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Travel & Hotel Pay Rates */}
          <div className="space-y-2">
            <Label className="text-slate-400 text-xs uppercase tracking-wider">Travel & Hotel Day Rates</Label>
            <p className="text-xs text-slate-500">These rates auto-fill into trips linked to this contract.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Travel Day Rate (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.travel_day_rate}
                  onChange={e => update('travel_day_rate', e.target.value)}
                  placeholder="e.g. 150"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="contract-travel-rate"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Hotel Day Rate (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.hotel_day_rate}
                  onChange={e => update('hotel_day_rate', e.target.value)}
                  placeholder="e.g. 120"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="contract-hotel-rate"
                />
              </div>
            </div>
          </div>

          {/* P45 Section */}
          <div className="space-y-2">
            <Label className="text-slate-400 text-xs uppercase tracking-wider">P45 (if employment ended early)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">P45 Date</Label>
                <Input
                  type="date"
                  value={form.p45_date}
                  onChange={e => update('p45_date', e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="contract-p45-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">P45 Earnings (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.p45_earnings}
                  onChange={e => update('p45_earnings', e.target.value)}
                  placeholder="Total earned"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="contract-p45-earnings"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-slate-300 text-sm">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={e => update('notes', e.target.value)}
              placeholder="Any additional notes..."
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="contract-save-btn"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Update' : 'Create'} Contract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractDialog;
